import { SESClient, SendEmailCommand } from '@aws-sdk/client-ses';
import { BaseEmailProvider } from '../BaseEmailProvider';
import type { EmailData, RegistrationNotificationData, EmailResponse } from '../types';

/**
 * AWS SES Email Provider
 */
export class AWSSESProvider extends BaseEmailProvider {
  protected providerName = 'AWS_SES';
  private sesClient: SESClient;

  constructor() {
    super();
    this.sesClient = this.createSESClient();
  }

  /**
   * Create SES client with configuration
   */
  private createSESClient(): SESClient {
    return new SESClient({
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
      credentials: {
        accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID || '',
        secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY || '',
      },
    });
  }

  /**
   * Validate AWS SES configuration
   */
  validateConfiguration(): boolean {
    const accessKeyId = import.meta.env.VITE_AWS_ACCESS_KEY_ID;
    const secretAccessKey = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY;
    const region = import.meta.env.VITE_AWS_REGION;

    if (!accessKeyId || !secretAccessKey) {
      return false;
    }

    // Basic format validation
    const accessKeyPattern = /^AKIA[0-9A-Z]{16}$/;
    if (!accessKeyPattern.test(accessKeyId)) {
      return false;
    }

    if (secretAccessKey.length !== 40) {
      return false;
    }

    return true;
  }

  /**
   * Send contact email via AWS SES
   */
  async sendEmail(data: EmailData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'contact');

      if (!this.validateConfiguration()) {
        throw new Error('AWS SES configuration is invalid. Check your credentials.');
      }

      const emailContent = this.generateContactEmailContent(data);
      
      const command = new SendEmailCommand({
        Source: emailContent.fromEmail,
        Destination: {
          ToAddresses: [emailContent.toEmail],
        },
        Message: {
          Subject: {
            Data: emailContent.subject,
            Charset: 'UTF-8',
          },
          Body: {
            Html: {
              Data: emailContent.htmlContent,
              Charset: 'UTF-8',
            },
            Text: {
              Data: emailContent.textContent,
              Charset: 'UTF-8',
            },
          },
        },
        ReplyToAddresses: [data.userEmail],
      });

      const result = await this.sesClient.send(command);
      
      this.logEmailSuccess(result.MessageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Email successfully sent to Fabrik 28',
        messageId: result.MessageId
      };

    } catch (error: any) {
      this.logEmailError(error, data.to);
      
      return {
        success: false,
        message: `AWS SES error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send registration notification via AWS SES
   */
  async sendRegistrationNotification(data: RegistrationNotificationData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'registration');

      if (!this.validateConfiguration()) {
        throw new Error('AWS SES configuration is invalid. Check your credentials.');
      }

      const emailContent = this.generateRegistrationEmailContent(data);
      
      const command = new SendEmailCommand({
        Source: emailContent.fromEmail,
        Destination: {
          ToAddresses: [emailContent.toEmail],
        },
        Message: {
          Subject: {
            Data: emailContent.subject,
            Charset: 'UTF-8',
          },
          Body: {
            Html: {
              Data: emailContent.htmlContent,
              Charset: 'UTF-8',
            },
            Text: {
              Data: emailContent.textContent,
              Charset: 'UTF-8',
            },
          },
        },
        ReplyToAddresses: [data.userEmail],
      });

      const result = await this.sesClient.send(command);
      
      this.logEmailSuccess(result.MessageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Registration notification sent successfully via AWS SES',
        messageId: result.MessageId
      };

    } catch (error: any) {
      this.logEmailError(error, this.getToEmail());
      
      return {
        success: false,
        message: `AWS SES error: ${error.message || 'Unknown error'}`
      };
    }
  }
}
